'use client';

import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { Check, ChevronRight, Circle } from 'lucide-react';
import * as React from 'react';

import { cn } from '@ui/lib/utils';
import { buttonVariants } from './button';

const DropdownMenu = DropdownMenuPrimitive.Root;

const DropdownMenuTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Trigger> & {
    asChild?: boolean;
  }
>(({ className, asChild = false, ...props }, ref) => {
  if (asChild) {
    return (
      <DropdownMenuPrimitive.Trigger
        ref={ref}
        className={cn('cursor-pointer outline-none', className)}
        {...props}
      />
    );
  }

  return (
    <DropdownMenuPrimitive.Trigger
      ref={ref}
      className={cn(
        'cursor-pointer outline-none',
        buttonVariants({ variant: 'outline', size: 'sm' }),
        className
      )}
      {...props}
    />
  );
});
DropdownMenuTrigger.displayName = DropdownMenuPrimitive.Trigger.displayName;

const DropdownMenuGroup = DropdownMenuPrimitive.Group;

const DropdownMenuPortal = DropdownMenuPrimitive.Portal;

const DropdownMenuSub = DropdownMenuPrimitive.Sub;

const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

const DropdownMenuSubTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> & {
    inset?: boolean;
  }
>(({ className, inset, children, ...props }, ref) => (
  <DropdownMenuPrimitive.SubTrigger
    ref={ref}
    className={cn(
      'flex cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none',
      'transition-colors duration-200 ease-in-out',
      'hover:bg-slate-50/80 transition duration-200 ease-in-out focus:bg-accent data-[state=open]:bg-accent z-60',
      inset && 'pl-8',
      className
    )}
    {...props}
  >
    {children}
    <ChevronRight className="ml-auto h-4 w-4" />
  </DropdownMenuPrimitive.SubTrigger>
));
DropdownMenuSubTrigger.displayName =
  DropdownMenuPrimitive.SubTrigger.displayName;

const DropdownMenuSubContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubContent>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.SubContent
    ref={ref}
    className={cn(
      'z-50 min-w-[8rem] overflow-hidden rounded-lg border bg-popover p-1 text-popover-foreground shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
      className
    )}
    {...props}
  />
));
DropdownMenuSubContent.displayName =
  DropdownMenuPrimitive.SubContent.displayName;

const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content> & {
    /**
     * Preferred alignment of the dropdown menu relative to the trigger
     * On mobile/tablet (< 1024px), 'center' alignment is automatically converted to 'start' to prevent off-screen issues
     * @default "center"
     */
    align?: 'start' | 'center' | 'end';
    /**
     * Offset distance from the alignment edge
     * @default 0
     */
    alignOffset?: number;
    /**
     * Fine-tune edge alignment compensation for different trigger button styles
     * When set, overrides the default edge alignment compensation
     * Useful for triggers with unusual padding or border styles
     * @default undefined (uses automatic compensation)
     */
    edgeAlignmentOffset?: number;
    /**
     * Horizontal position override that will take precedence over align
     * Controls whether the menu is positioned to the left, right, or automatically determined
     * @default "auto"
     */
    horizontalPosition?: 'left' | 'right' | 'auto';
    /**
     * Responsive alignment behavior
     * When true, aligns to right edge on desktop (lg+) and left edge on mobile/tablet
     * @default false
     */
    responsiveAlign?: boolean;
  }
>(
  (
    {
      className,
      sideOffset = 4,
      align = 'center',
      alignOffset = 0,
      edgeAlignmentOffset,
      horizontalPosition = 'auto',
      responsiveAlign = false,
      ...props
    },
    ref
  ) => {
    // Hook to detect screen size for responsive alignment
    const [isMobileTablet, setIsMobileTablet] = React.useState(false);

    React.useEffect(() => {
      const checkMobileTablet = () => {
        setIsMobileTablet(window.innerWidth < 1024); // lg breakpoint - includes mobile and tablet
      };

      checkMobileTablet();
      window.addEventListener('resize', checkMobileTablet);
      return () => window.removeEventListener('resize', checkMobileTablet);
    }, []);

    // Calculate final alignment based on responsive behavior, horizontalPosition override, and screen size
    // Best practice: Never use center alignment on mobile/tablet as it can cause off-screen issues
    const finalAlign = React.useMemo(() => {
      if (responsiveAlign) {
        return isMobileTablet ? 'start' : 'end';
      }

      if (horizontalPosition !== 'auto') {
        return horizontalPosition === 'left' ? 'start' : 'end';
      }

      // On mobile/tablet, never allow center alignment - default to edge alignment
      if (isMobileTablet && align === 'center') {
        return 'start'; // Default to left edge on mobile/tablet
      }

      return align;
    }, [align, horizontalPosition, responsiveAlign, isMobileTablet]);

    // Calculate final alignOffset to ensure perfect edge alignment
    // Compensate for dropdown's internal styling and typical trigger button padding
    const finalAlignOffset = React.useMemo(() => {
      // If user provided explicit alignOffset, use it as-is (no automatic compensation)
      if (alignOffset !== 0) {
        return alignOffset;
      }

      // If user provided specific edge alignment offset, use that
      if (edgeAlignmentOffset !== undefined) {
        return edgeAlignmentOffset;
      }

      // For edge alignment, apply default compensation for visual spacing differences
      // Accounts for dropdown border (1px) + padding (4px) + typical trigger padding offset
      if (finalAlign === 'start') {
        return 7; // Negative offset to align left edges perfectly
      }
      if (finalAlign === 'end') {
        return -7; // Positive offset to align right edges perfectly
      }

      // For center alignment, no offset needed
      return 0;
    }, [alignOffset, finalAlign, edgeAlignmentOffset]);

    return (
      <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.Content
          ref={ref}
          sideOffset={sideOffset}
          align={finalAlign}
          alignOffset={finalAlignOffset}
          className={cn(
            'z-50 min-w-[8rem] overflow-hidden rounded-lg border bg-popover p-1 text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
            className
          )}
          {...props}
        />
      </DropdownMenuPrimitive.Portal>
    );
  }
);
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

const DropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
    inset?: boolean;
  }
>(({ className, inset, ...props }, ref) => (
  <DropdownMenuPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex cursor-pointer text-slate-800 min-w-[140px] select-none items-center rounded-sm px-2 py-2 pr-3 text-sm outline-none',
      'transition-colors duration-200 ease-in-out',
      'hover:bg-slate-50/80 transition duration-200 ease-in-out data-[highlighted]:bg-slate-200',
      'focus:bg-accent focus:text-accent-foreground',
      'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      inset && 'pl-8',
      className
    )}
    {...props}
  />
));
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;

const DropdownMenuCheckboxItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem>
>(({ className, children, checked, ...props }, ref) => (
  <DropdownMenuPrimitive.CheckboxItem
    ref={ref}
    className={cn(
      'relative flex cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none',
      'transition-colors duration-200 ease-in-out',
      'hover:bg-slate-50/80 transition duration-200 ease-in-out focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      className
    )}
    checked={checked}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <DropdownMenuPrimitive.ItemIndicator>
        <Check className="h-4 w-4" />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </DropdownMenuPrimitive.CheckboxItem>
));
DropdownMenuCheckboxItem.displayName =
  DropdownMenuPrimitive.CheckboxItem.displayName;

const DropdownMenuRadioItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem>
>(({ className, children, ...props }, ref) => (
  <DropdownMenuPrimitive.RadioItem
    ref={ref}
    className={cn(
      'relative flex cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none',
      'transition-colors duration-200 ease-in-out',
      'hover:bg-slate-50/80 transition duration-200 ease-in-out focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      className
    )}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <DropdownMenuPrimitive.ItemIndicator>
        <Circle className="h-2 w-2 fill-current" />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </DropdownMenuPrimitive.RadioItem>
));
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;

const DropdownMenuLabel = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & {
    inset?: boolean;
  }
>(({ className, inset, ...props }, ref) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={cn('px-2 py-1.5 text-sm font-bold', inset && 'pl-8', className)}
    {...props}
  />
));
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;

const DropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cn('border-t border-slate-200 blur-[0.8px]', className)}
    {...props}
  />
));
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;

const DropdownMenuShortcut = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => {
  return (
    <span
      className={cn('border-t border-slate-200 blur-[0.8px]', className)}
      {...props}
    />
  );
};
DropdownMenuShortcut.displayName = 'DropdownMenuShortcut';

export {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
};
